import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import _ from 'lodash';

import { SCHEDULE_MODE } from '../../constants';
import { RESERVATION_GUEST_TYPES, getReservationGuestText } from '../../../utils/reservations';
import { addFlyout, FLYOUT_TYPES } from '../../../utils/flyout';

import './reservation.scss';

const onReservationClick = reservation => () => {
  addFlyout(FLYOUT_TYPES.RESERVATION_DETAILS, {
    reservationId: _.get(reservation, 'id'),
  });
};

class Reservation extends PureComponent {
  constructor(props) {
    super(props);

    this.CLASS_NAME = 'brzw-calendar-reservation';

    this.renderReservationBar = this.renderReservationBar.bind(this);
  }

  renderReservationBar(className, reservation, baseClassName) {
    const { numberOfOccupied } = this.props;
    const typeGuestCode = _.get(reservation, 'type_guest.code', '');

    const reservationClassName = classnames(className, {
      [`${this.CLASS_NAME}__multiple-occupied`]: numberOfOccupied > 1,
      [`${baseClassName}--on-hold`]: typeGuestCode === RESERVATION_GUEST_TYPES.HOLD,
    });

    if (reservation) {
      return (
        <button
          className={classnames(
            `${this.CLASS_NAME}__clickable-wrapper`,
            'brzw-button',
            reservationClassName,
          )}
          onClick={onReservationClick(reservation)}
          type="button"
        />
      );
    }

    return <div className={reservationClassName} />;
  }

  render() {
    const {
      containerClassName: propsContainerClassName,
      checkin,
      checkout,
      checkinHightlighted,
      checkoutHightlighted,
      empty,
      expanded,
      firstDataInRow,
      grayedOut,
      mode,
      nextReservationDate,
      nextReservationReadyStatus,
      occupied,
      occupiedHightlighted,
    } = this.props;

    if (!occupied && !checkin && !checkout && !empty) {
      return null;
    }

    let readiness = null;
    let readinessIsNotNull = false;

    const grayBgr = checkin || checkout || occupied;
    const checkInOnly = checkin && !checkout && !occupied;

    const blueBgr =
      (checkin && checkinHightlighted) ||
      (checkout && checkoutHightlighted) ||
      (occupied && occupiedHightlighted);

    const darkGrayBgr =
      (checkin && grayedOut && !checkinHightlighted) ||
      (checkout && grayedOut && !checkoutHightlighted) ||
      (occupied && grayedOut && !occupiedHightlighted);

    const containerClassName = classnames(
      this.CLASS_NAME,
      propsContainerClassName,
      `${this.CLASS_NAME}--${mode}-mode`,
      {
        [`${this.CLASS_NAME}--blue-bgr`]: blueBgr,
        [`${this.CLASS_NAME}--curled`]: !expanded,
        [`${this.CLASS_NAME}--dark-gray-bgr`]: darkGrayBgr,
        [`${this.CLASS_NAME}--empty`]: empty,
        [`${this.CLASS_NAME}--gray-bgr`]: grayBgr,
        [`${this.CLASS_NAME}--imposed-checkin`]: checkin && occupied,
        [`${this.CLASS_NAME}--imposed-checkout`]: checkout && occupied,
        [`${this.CLASS_NAME}--checkin-only`]: checkInOnly,
      },
    );

    const checkinDate = _.get(checkin, 'checkin_date', null);
    const isDaily = mode === SCHEDULE_MODE.DAILY;

    if (checkinDate && nextReservationDate) {
      const checkinMoment = moment(checkinDate);
      const nextDateMoment = moment(nextReservationDate);

      if (checkinMoment.isSame(nextDateMoment)) {
        readiness = nextReservationReadyStatus;
        readinessIsNotNull = readiness !== null;
      }
    }

    const checkinWrapperClassName = classnames(`${this.CLASS_NAME}__checkin-wrapper`, {
      [`${this.CLASS_NAME}__checkin-wrapper--highlight`]: checkinHightlighted,
      [`${this.CLASS_NAME}__checkin-wrapper--gray-out`]: grayedOut && !checkinHightlighted,
    });

    const checkoutWrapperClassName = classnames(`${this.CLASS_NAME}__checkout-wrapper`, {
      [`${this.CLASS_NAME}__checkout-wrapper--highlight`]: checkoutHightlighted,
      [`${this.CLASS_NAME}__checkout-wrapper--gray-out`]: grayedOut && !checkoutHightlighted,
    });

    const occupiedClassName = classnames(`${this.CLASS_NAME}__occupied-wrapper`, {
      [`${this.CLASS_NAME}__occupied-wrapper--highlight`]: occupiedHightlighted,
      [`${this.CLASS_NAME}__occupied-wrapper--gray-out`]: grayedOut && !occupiedHightlighted,
    });

    const buttonsContainerClassName = classnames(`${this.CLASS_NAME}__container`, {
      [`${this.CLASS_NAME}__container--checkin-not-ready`]: readinessIsNotNull && !readiness,
      [`${this.CLASS_NAME}__container--checkin-ready`]: readinessIsNotNull && readiness,
      [`${this.CLASS_NAME}__container--with-separator`]: checkin || checkout,
    });

    const reservation = _.isEmpty(checkin) ? occupied : checkin;
    const reservationText = getReservationGuestText(reservation, true);

    return (
      <div className={containerClassName}>
        {((occupied && isDaily) || checkin || (firstDataInRow && !checkout)) && (
          <span
            className={classnames(`${this.CLASS_NAME}__text`, {
              [`${this.CLASS_NAME}__text--occupied`]: occupied,
              [`${this.CLASS_NAME}__text--daily`]: isDaily,
              [`${this.CLASS_NAME}__text--highlight`]: occupiedHightlighted || checkinHightlighted,
              [`${this.CLASS_NAME}__text--gray-out`]:
                grayedOut && !(occupiedHightlighted || checkinHightlighted),
            })}
            title={reservationText}
          >
            {reservationText}
          </span>
        )}
        <div className={buttonsContainerClassName}>
          {empty && <div className={`${this.CLASS_NAME}__empty-wrapper`} />}
          {checkout &&
            this.renderReservationBar(
              checkoutWrapperClassName,
              checkout,
              `${this.CLASS_NAME}__checkout-wrapper`,
            )}
          {checkin &&
            this.renderReservationBar(
              checkinWrapperClassName,
              checkin,
              `${this.CLASS_NAME}__checkin-wrapper`,
            )}
          {occupied &&
            this.renderReservationBar(
              occupiedClassName,
              occupied,
              `${this.CLASS_NAME}__occupied-wrapper`,
            )}
        </div>
      </div>
    );
  }
}

Reservation.propTypes = {
  checkin: PropTypes.shape({}),
  checkinHightlighted: PropTypes.bool,
  checkout: PropTypes.shape({}),
  checkoutHightlighted: PropTypes.bool,
  containerClassName: PropTypes.string,
  empty: PropTypes.bool,
  expanded: PropTypes.bool,
  firstDataInRow: PropTypes.bool,
  grayedOut: PropTypes.bool,
  mode: PropTypes.string,
  nextReservationDate: PropTypes.string,
  nextReservationReadyStatus: PropTypes.any,
  numberOfOccupied: PropTypes.number,
  occupied: PropTypes.shape({}),
  occupiedHightlighted: PropTypes.bool,
};

Reservation.defaultProps = {
  checkin: null,
  checkinHightlighted: false,
  checkout: null,
  checkoutHightlighted: false,
  containerClassName: '',
  empty: false,
  expanded: false,
  firstDataInRow: false,
  grayedOut: false,
  mode: SCHEDULE_MODE.DAILY,
  nextReservationDate: '',
  nextReservationReadyStatus: null,
  numberOfOccupied: 0,
  occupied: null,
  occupiedHightlighted: null,
};

export default Reservation;
